import { observer } from 'mobx-react-lite'
import { ResponsesCardContainer } from '@/features/jobs/details/components/ResponsesCard/ResponsesCardContainer'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { InfoLinesCardContainer } from '@/features/jobs/details/components/InfoLinesCard/InfoLinesCardContainer'
import { EventsCardContainer } from '@/features/jobs/details/components/EventsCard/EventsCardContainer'
import { DocumentsCardContainer } from '@/features/jobs/details/components/DocumentsCard/DocumentsCardContainer'
import {
  DetailsDocumentationStack,
  DetailsView,
  JobDetailsView,
} from '@/features/jobs/details/components/DetailsView'
import { AmendmentsCardContainer } from '@/features/jobs/details/components/AmendmentsCard/AmendmentsCardContainer'
import { CapturedInformationCardContainer } from '@/features/jobs/details/components/CapturedInformationCard/CapturedInformationCardContainer'
import { FinalReturnAmountsCardContainer } from '@/features/jobs/details/components/FinalReturnAmountsCard/FinalReturnAmountsCardContainer'
import { JobScopeChangeRequestsCardContainer } from '@/features/jobs/details/components/JobScopeChangeRequestsCard/JobScopeChangeRequestsCardContainer'
import { useDocumentTitle } from '@/hooks/document-title'
import { TxfJobCardContainer } from '@/features/jobs/details/components/TxfJobCard/TxfJobCardContainer'

export interface DetailsViewContainerProps {
  vm: JobDetailsViewModel
}

export const DetailsViewContainer = observer(function DetailsViewContainer({
  vm,
}: DetailsViewContainerProps) {
  return vm.showDraftView ? (
    <DraftViewContainer vm={vm} />
  ) : (
    <JobViewContainer vm={vm} />
  )
})

const DraftViewContainer = observer(function DraftViewContainer({
  vm,
}: DetailsViewContainerProps) {
  return (
    <DetailsView>
      {vm.detailsVm.canViewCapturedInfoCard && (
        <CapturedInformationCardContainer vm={vm} />
      )}

      {vm.canViewDocs && <DocumentsCardContainer vm={vm} />}
      {vm.actionsVm.canViewAmendments && <AmendmentsCardContainer vm={vm} />}
    </DetailsView>
  )
})

const JobViewContainer = observer(function JobViewContainer({
  vm,
}: DetailsViewContainerProps) {
  useDocumentTitle(`${vm.job.workspace.name} | ${vm.job.displayName}`)

  return (
    <JobDetailsView
      onboarding={
        vm.detailsVm.canViewCapturedInfoCard ? (
          <CapturedInformationCardContainer vm={vm} />
        ) : (
          <ResponsesCardContainer vm={vm} />
        )
      }
      documentation={<DocumentationSectionContainer vm={vm} />}
    >
      {vm.job.infoLines.length > 0 && <InfoLinesCardContainer vm={vm} />}

      {vm.finalReturnAmountsVm.canView && (
        <FinalReturnAmountsCardContainer vm={vm.finalReturnAmountsVm} />
      )}

      {vm.actionsVm.canViewAmendments && <AmendmentsCardContainer vm={vm} />}

      {vm.scopeChangesVm.canViewScopeChangeRequests &&
        vm.scopeChangesVm.scopeChangeRequests.length > 0 &&
        !vm.showDraftView && <JobScopeChangeRequestsCardContainer vm={vm} />}

      <EventsCardContainer vm={vm} />
    </JobDetailsView>
  )
})

const DocumentationSectionContainer = observer(
  function DocumentationSectionContainer({ vm }: DetailsViewContainerProps) {
    if (vm.documentsVm.txfJobVm.canView && vm.canViewDocs) {
      return (
        <DetailsDocumentationStack>
          {vm.documentsVm.txfJobVm.canView && (
            <TxfJobCardContainer vm={vm.documentsVm.txfJobVm} />
          )}
          {vm.canViewDocs && <DocumentsCardContainer vm={vm} />}
        </DetailsDocumentationStack>
      )
    }

    if (vm.documentsVm.txfJobVm.canView) {
      return <TxfJobCardContainer vm={vm.documentsVm.txfJobVm} />
    }

    if (vm.canViewDocs) {
      return <DocumentsCardContainer vm={vm} />
    }
  },
)
